/**
 * Negotiation API client.
 * 
 * Provides fetch helpers and EventSource wrapper for SSE.
 */

import { API_BASE } from "../config";

/**
 * Get list of available providers and models.
 */
export async function getProviders() {
  const response = await fetch(`${API_BASE}/negotiation/providers`);
  if (!response.ok) {
    throw new Error(`Failed to fetch providers: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Start a new negotiation.
 */
export async function startNegotiation(config) {
  const response = await fetch(`${API_BASE}/negotiation/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to start negotiation");
  }

  return response.json();
}

/**
 * Stream negotiation events via SSE.
 * 
 * @param {string} negotiationId - ID of the negotiation
 * @param {function} onEvent - Callback for each event
 * @param {function} onError - Callback for errors
 * @returns {EventSource} EventSource instance (caller should close it)
 */
export function streamNegotiation(negotiationId, onEvent, onError) {
  const eventSource = new EventSource(`${API_BASE}/negotiation/stream/${negotiationId}`);

  // Handle different event types (including agent_chunk for word-by-word streaming)
  const eventTypes = ["context_pack", "turn_start", "agent_start", "agent_chunk", "agent_message", "consensus_start", "deal", "error"];

  eventTypes.forEach((type) => {
    eventSource.addEventListener(type, (e) => {
      try {
        const payload = JSON.parse(e.data);
        onEvent({ type, payload });
      } catch (error) {
        console.error(`Failed to parse ${type} event:`, error);
      }
    });
  });

  eventSource.onerror = (e) => {
    console.error("SSE error:", e);
    if (onError) {
      onError(e);
    }
  };

  return eventSource;
}

/**
 * Get comparison data: final proposals vs consensus deal.
 */
export async function getCompare(negotiationId) {
  const response = await fetch(`${API_BASE}/negotiation/compare/${negotiationId}`, {
    method: "POST",
  });

  if (!response.ok) {
    throw new Error("Failed to get comparison data");
  }

  return response.json();
}

/**
 * Get full negotiation receipt/transcript.
 */
export async function getReceipt(negotiationId) {
  const response = await fetch(`${API_BASE}/negotiation/receipt/${negotiationId}`);

  if (!response.ok) {
    throw new Error("Failed to get receipt");
  }

  return response.json();
}

/**
 * Add human input during negotiation.
 */
export async function addHumanInput(negotiationId, message) {
  const response = await fetch(`${API_BASE}/negotiation/${negotiationId}/human-input`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to send human input");
  }

  return response.json();
}
